import { EditOutlined, PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { useAccess } from '@umijs/max';
import { useTenant } from '@zhiyue/core';
import {
  Button,
  Form,
  Modal,
  message,
  Popover,
  Radio,
  Space,
  Tag,
  Tooltip,
  TreeSelect,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CrmTaggableEntityType,
  CrmTagSummary,
  CrmTaxonomy,
  CrmTaxonomyTag,
} from '@/services/bff/taxonomy-management.api';
import {
  batchEntityTags,
  queryEntityTagOptions,
  quickCreateEntityTag,
  replaceEntityTags,
} from '@/services/bff/taxonomy-management.api';
import { buildTagTreeData, normalizeTagPath } from './helpers';

export type { CrmTaggableEntityType, CrmTagSummary };

export function CrmTagSelector({
  value,
  onChange,
  disabled,
  placeholder = '选择或输入标签，多级用 / 分隔',
  allowClear = true,
  entityType,
  canCreate,
  onCreateOpenChange,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  entityType?: CrmTaggableEntityType;
  canCreate?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
}) {
  const access = (useAccess() ?? {}) as Record<string, boolean>;
  const { currentTenantUuid } = useTenant();
  const prefix = entityType
    ? {
        lead: 'leads-',
        customer: 'customers-',
        contact: 'contacts-',
        opportunity: 'opportunities-',
      }[entityType]
    : undefined;
  const allowed = Boolean(
    prefix &&
      Object.keys(access).some(
        (key) =>
          key.startsWith(prefix) &&
          (key.endsWith(':create') ||
            key.endsWith(':edit') ||
            key.endsWith(':*')) &&
          access[key] &&
          access[key.split(':')[0]],
      ),
  );
  const creatable = Boolean(entityType) && (canCreate ?? allowed) && !disabled;
  const [saving, setSaving] = useState(false);
  const pending = useRef<symbol | null>(null);
  const context = useRef(0);
  const composing = useRef(false);
  const navigating = useRef(false);
  const latestValue = useRef(value || []);
  latestValue.current = value || [];
  const [createdLabels, setCreatedLabels] = useState<Record<string, string>>(
    {},
  );
  const [treeOpen, setTreeOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener('crm-tags-changed', refresh);
    return () => window.removeEventListener('crm-tags-changed', refresh);
  }, []);
  useEffect(() => {
    onCreateOpenChange?.(saving);
    return () => onCreateOpenChange?.(false);
  }, [saving, onCreateOpenChange]);
  useEffect(() => {
    context.current += 1;
    pending.current = null;
    setSaving(false);
    setCreatedLabels({});
    setTreeOpen(false);
    setSearch('');
    setTaxonomies([]);
    return () => {
      context.current += 1;
    };
  }, [currentTenantUuid]);
  const [loading, setLoading] = useState(false);
  const [taxonomies, setTaxonomies] = useState<
    Array<CrmTaxonomy & { tags: CrmTaxonomyTag[] }>
  >([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void queryEntityTagOptions()
      .then((result) => {
        if (active) setTaxonomies(result || []);
      })
      .catch((error) => {
        if (!active) return;
        message.error(
          error instanceof Error ? error.message : '标签选项加载失败',
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision, currentTenantUuid]);

  const treeData = useMemo(() => buildTagTreeData(taxonomies), [taxonomies]);
  const labels = useMemo(() => {
    const result = new Map<string, string>();
    const walk = (nodes: typeof treeData) =>
      nodes.forEach((node) => {
        result.set(node.value, node.label || node.title);
        walk(node.children);
      });
    walk(treeData);
    return result;
  }, [treeData]);
  const normalized = normalizeTagPath(search);
  const exact =
    normalized.path && !normalized.error
      ? Array.from(new Map([...labels, ...Object.entries(createdLabels)])).find(
          ([, label]) => normalizeTagPath(label).path === normalized.path,
        )?.[0]
      : undefined;
  const choose = (uuid: string) => {
    onChange?.(Array.from(new Set([...latestValue.current, uuid])));
    setSearch('');
    navigating.current = false;
  };
  const create = async () => {
    if (pending.current || !creatable || !entityType) return;
    if (normalized.error) {
      message.error(normalized.error);
      return;
    }
    if (!normalized.path) return;
    if (exact) {
      choose(exact);
      return;
    }
    const request = Symbol();
    const version = context.current;
    pending.current = request;
    setSaving(true);
    try {
      const result = await quickCreateEntityTag({
        entityType,
        name: normalized.path,
      });
      window.dispatchEvent(new Event('crm-tags-changed'));
      if (context.current !== version) return;
      setCreatedLabels((current) => ({
        ...current,
        [result.tagUuid]: result.namePath || normalized.path,
      }));
      choose(result.tagUuid);
      message.success(result.reused ? '已选中已有标签' : '标签已创建并选中');
    } catch (error) {
      if (context.current === version)
        message.error(
          error instanceof Error ? error.message : '标签创建失败，请重试',
        );
    } finally {
      if (pending.current === request) {
        pending.current = null;
        if (context.current === version) setSaving(false);
      }
    }
  };
  const displayTree = [
    ...treeData,
    ...Object.entries(createdLabels)
      .filter(([uuid]) => !labels.has(uuid))
      .map(([uuid, label]) => ({
        value: uuid,
        title: label,
        label,
        children: [],
      })),
  ];
  return (
    <div
      onCompositionStartCapture={() => {
        composing.current = true;
      }}
      onCompositionEndCapture={() => {
        composing.current = false;
      }}
      onKeyDownCapture={(event) => {
        if ((event.target as HTMLElement).getAttribute('role') !== 'combobox')
          return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          navigating.current = Array.from(labels.values()).some((label) =>
            label
              .replace(/\s/gu, '')
              .toLowerCase()
              .includes(normalized.path.toLowerCase()),
          );
        }
        if (event.key !== 'Enter') return;
        const native = event.nativeEvent;
        if (composing.current || native.isComposing || native.keyCode === 229) {
          event.stopPropagation();
          return;
        }
        // Never submit the surrounding business form while confirming a tag.
        event.preventDefault();
        if (pending.current) {
          event.stopPropagation();
          return;
        }
        if (search && !navigating.current && (creatable || exact)) {
          event.stopPropagation();
          if (exact) choose(exact);
          else void create();
        }
      }}
      style={{ width: '100%', minWidth: 0 }}
    >
      <TreeSelect
        multiple
        value={value}
        onChange={(next) => {
          onChange?.(next);
          setSearch('');
          navigating.current = false;
        }}
        disabled={disabled}
        loading={loading || saving}
        allowClear={allowClear}
        showSearch
        searchValue={search}
        autoClearSearchValue={false}
        filterTreeNode={(input, node) =>
          String(node.label || '')
            .replace(/\s/gu, '')
            .toLowerCase()
            .includes(input.replace(/\s/gu, '').toLowerCase())
        }
        treeNodeLabelProp="label"
        notFoundContent={
          creatable && search && !exact ? <span aria-hidden /> : undefined
        }
        onSearch={(text) => {
          setSearch(text);
          navigating.current = false;
        }}
        open={treeOpen}
        onOpenChange={(open) => {
          if (!saving) setTreeOpen(open);
          if (open) setRevision((revision) => revision + 1);
        }}
        placeholder={placeholder}
        treeData={displayTree}
        treeDefaultExpandAll
        treeLine
        maxTagCount="responsive"
        style={{ width: '100%' }}
        popupRender={(menu) => (
          <>
            {creatable &&
              search &&
              !exact &&
              (normalized.error ? (
                <div
                  role="alert"
                  style={{
                    padding: '8px 12px',
                    color: 'var(--ant-color-error)',
                  }}
                >
                  {normalized.error}
                </div>
              ) : (
                <Button
                  type="text"
                  block
                  loading={saving}
                  disabled={loading || saving}
                  icon={<PlusOutlined />}
                  style={{
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    height: 'auto',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void create()}
                >
                  创建：{normalized.path}
                </Button>
              ))}
            {menu}
          </>
        )}
      />
    </div>
  );
}

export function CrmTagDisplay({
  tags,
  emptyText = '-',
}: {
  tags?: CrmTagSummary[];
  emptyText?: React.ReactNode;
}) {
  if (!tags?.length) return <>{emptyText}</>;
  return (
    <Space size={[4, 4]} wrap>
      {tags.map((tag) => (
        <Tooltip
          key={tag.uuid}
          title={`${tag.namePath || tag.name}${tag.enabled ? '' : ' · 已停用'}`}
        >
          <Tag
            color={tag.backgroundColor || tag.color}
            style={!tag.enabled ? { opacity: 0.65 } : undefined}
          >
            {tag.name}
          </Tag>
        </Tooltip>
      ))}
    </Space>
  );
}

export function EntityTagEditor({
  entityType,
  entityUuid,
  tags,
  editable = true,
  onChange,
}: {
  entityType: CrmTaggableEntityType;
  entityUuid?: string;
  tags?: CrmTagSummary[];
  editable?: boolean;
  onChange?: (tags: CrmTagSummary[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string[]>([]);
  const [displayTags, setDisplayTags] = useState<CrmTagSummary[]>(tags || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDisplayTags(tags || []);
    setValue((tags || []).map((item) => item.uuid));
  }, [tags]);

  return (
    <Space size={8} wrap>
      <CrmTagDisplay tags={displayTags} emptyText="—" />
      {editable && entityUuid && (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => setOpen(true)}
        >
          维护标签
        </Button>
      )}
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        title="维护标签"
        open={open}
        confirmLoading={saving}
        onCancel={() => setOpen(false)}
        onOk={async () => {
          if (!entityUuid) return;
          setSaving(true);
          try {
            const next = await replaceEntityTags(entityType, entityUuid, value);
            message.success('标签已保存');
            setDisplayTags(next);
            onChange?.(next);
            setOpen(false);
          } catch (error) {
            message.error(
              error instanceof Error ? error.message : '标签保存失败',
            );
          } finally {
            setSaving(false);
          }
        }}
      >
        <CrmTagSelector
          entityType={entityType}
          canCreate={editable}
          value={value}
          onChange={setValue}
        />
      </Modal>
    </Space>
  );
}

export function TagFilterControl({
  responsive = false,
  entityType,
  canCreate,
  onCreateOpenChange,
  tagUuids,
  tagMatch = 'any',
  onChange,
}: {
  responsive?: boolean;
  tagUuids?: string[];
  tagMatch?: 'any' | 'all';
  onCreateOpenChange?: (open: boolean) => void;
  entityType?: CrmTaggableEntityType;
  canCreate?: boolean;
  onChange: (value: { tagUuids: string[]; tagMatch: 'any' | 'all' }) => void;
}) {
  return (
    <Space
      orientation="vertical"
      size={6}
      style={{
        minWidth: responsive ? 0 : 280,
        width: responsive ? '100%' : undefined,
      }}
    >
      <CrmTagSelector
        canCreate={canCreate}
        entityType={entityType}
        onCreateOpenChange={onCreateOpenChange}
        value={tagUuids}
        onChange={(next) => onChange({ tagUuids: next, tagMatch })}
        placeholder="按标签筛选"
      />
      <Radio.Group
        size="small"
        style={
          responsive ? { display: 'flex', flexWrap: 'wrap', gap: 4 } : undefined
        }
        value={tagMatch}
        onChange={(event) =>
          onChange({ tagUuids: tagUuids || [], tagMatch: event.target.value })
        }
        options={[
          { label: '命中任一', value: 'any' },
          { label: '同时包含全部', value: 'all' },
        ]}
      />
    </Space>
  );
}

export function TagFilterButton({
  entityType,
  canCreate,
  value,
  onChange,
}: {
  entityType?: CrmTaggableEntityType;
  canCreate?: boolean;
  value: { tagUuids: string[]; tagMatch: 'any' | 'all' };
  onChange: (value: { tagUuids: string[]; tagMatch: 'any' | 'all' }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!creating) setOpen(next);
      }}
      trigger="click"
      placement="bottomRight"
      content={
        <TagFilterControl
          canCreate={canCreate}
          entityType={entityType}
          onCreateOpenChange={setCreating}
          tagUuids={value.tagUuids}
          tagMatch={value.tagMatch}
          onChange={onChange}
        />
      }
    >
      <Button icon={<TagsOutlined />}>
        标签筛选{value.tagUuids.length ? ` (${value.tagUuids.length})` : ''}
      </Button>
    </Popover>
  );
}

export function BatchEntityTagModal({
  open,
  entityType,
  canCreate,
  entityUuids,
  onCancel,
  onSuccess,
}: {
  open: boolean;
  entityType: CrmTaggableEntityType;
  canCreate?: boolean;
  entityUuids: string[];
  onCancel: () => void;
  onSuccess?: () => void;
}) {
  const [form] = Form.useForm<{
    operation: 'add' | 'remove';
    tagUuids: string[];
  }>();
  const [saving, setSaving] = useState(false);
  const submit = useCallback(async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await batchEntityTags({ entityType, entityUuids, ...values });
      message.success(`已为 ${entityUuids.length} 条记录批量更新标签`);
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '批量标签更新失败',
      );
    } finally {
      setSaving(false);
    }
  }, [entityType, entityUuids, form, onCancel, onSuccess]);

  const title = useMemo(
    () => (
      <Space>
        <TagsOutlined />
        批量添加或移除标签
      </Space>
    ),
    [],
  );

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      title={title}
      open={open}
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={() => void submit()}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ operation: 'add', tagUuids: [] }}
      >
        <Form.Item name="operation" label="操作" rules={[{ required: true }]}>
          <Radio.Group
            options={[
              { label: '添加', value: 'add' },
              { label: '移除', value: 'remove' },
            ]}
          />
        </Form.Item>
        <Form.Item name="tagUuids" label="标签" rules={[{ required: true }]}>
          <CrmTagSelector entityType={entityType} canCreate={canCreate} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
